/* eslint-disable @next/next/no-img-element */
import React from "react";
import { Formik, Form } from "formik";
import {
  TextField,
  NumberField,
  Checkbox,
  Button,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  Autocomplete,
  Select,
  TalkyChamp,
  AlertBox,
  PrivacyPolicyLink,
  TermsOfServiceLink,
  Dialog,
  Pressable,
  Toast,
} from "@gonoodle/gn-universe-ui";
import {
  useDebounce,
  concatenateQueryParams,
} from "@gonoodle/gn-universe-utils";
import Joi from "joi";
import { CheckIcon, SelectorIcon, RefreshIcon } from "@heroicons/react/solid";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  useMutation,
  useQueryClient,
  QueryClient,
  dehydrate,
} from "@tanstack/react-query";
import concat from "lodash/concat";
import { useRouter } from "next/router";
import { AnimatePresence, motion } from "framer-motion";
import {
  SECTIONS,
  SECTIONS_TYPES,
  PAGES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { forEach } from "lodash";
import { camelizeKeys } from "humps";

import ChampionSelect from "../components/ChampionSelect";
import Layout from "../components/Layout";
import GoNoodleHeader from "../components/GoNoodleHeader";
import SSOButton from "../components/SSOButton";
import {
  EmailField,
  PasswordField,
  DateField,
} from "../components/FormElements";
import ProfileType from "../components/ProfileType";
import ChampPointsStamp from "../components/ChampPointsStamp";
import {
  QUERY_KEYS,
  PROFILE_TYPES,
  ROUTE_PERMISSIONS,
  ROUTE_PATHS,
  ROUTE_PREFIX,
  CHAMP_NAMES,
  REGISTRATION_INITIATORS,
  SSO_ERRORS,
  OAUTH_INITIATORS,
  URLS,
} from "../constants";
import api from "../api";
import { withAuthorization } from "../containers";
import { useLogEvent } from "../contexts/Analytics";
import {
  getSourcePageAndSourcePageTypeFromReferrer,
  getUtmParamsFromQueryParams,
} from "../utils/analytics";
import {
  useLoggedOutChampPoints,
  useChampion,
  useValidateEmail,
  useLogin,
  useGradesQuery,
  useSchoolsFilterQuery,
  useIntendedPath,
} from "../hooks";
import { useUser } from "../contexts/user";
import Link from "../components/Link";
import RedirectToLoginLink from "../components/RedirectToLoginLink";
import RedirectToRegistrationButton from "../components/RedirectToRegistrationButton";

const JSURL = require("jsurl");

const client = api();

function LoginDialog({ title, description, email, onClose }) {
  const router = useRouter();
  const { validateEmail } = useValidateEmail();
  const { updateUserFlags } = useUser();
  const { resetLoggedOutChampPoints } = useLoggedOutChampPoints();
  const { oauthInitiator } = camelizeKeys(router.query);

  const fields = {
    email: "email",
    password: "password",
    importCleverProfiles: "importCleverProfiles",
  };

  const { logEvent: logCleverAccountConnectedEvent } = useLogEvent({
    event: "Clever Account Connected to GoNoodle Account",
    properties: {
      sourceElement: SECTIONS_TYPES.CLEVER,
    },
    options: {
      referrer: () => ({}),
    },
  });

  const { logEvent: logGoogleAccountConnectedEvent } = useLogEvent({
    event: "Google Account Connected to GoNoodle Account",
    properties: {
      sourceElement: SECTIONS_TYPES.GOOGLE,
    },
    options: {
      referrer: () => ({}),
    },
  });

  const { login, error, isLoading } = useLogin({
    redirectRoute: {
      pathname: `/${ROUTE_PREFIX.PROFILES}`,
      query: {
        registrationInitiator: REGISTRATION_INITIATORS.CLEVER,
      },
      referrer: {
        sourceElement: SECTIONS_TYPES.CLEVER,
        sourceName: SECTIONS_TYPES.CREATE_ACCOUNT,
      },
    },
    onSuccess: (user, formValues) => {
      if (oauthInitiator === OAUTH_INITIATORS.CLEVER_SIGNUP) {
        logCleverAccountConnectedEvent();
      } else if (oauthInitiator === OAUTH_INITIATORS.GOOGLE_SIGNUP) {
        logGoogleAccountConnectedEvent();
      }
      // TODO: Rollback flow if this fails
      updateUserFlags({
        importCleverProfiles: formValues[fields.importCleverProfiles],
      });
      resetLoggedOutChampPoints();
    },
  });

  return (
    <Dialog.Root isOpen={true} isDismissable={!isLoading} onClose={onClose}>
      <Dialog.Content className="w-full md:w-screen-xs p-md my-8 bg-white rounded-lg transform transition-all">
        <div className="flex flex-col space-y-md">
          <Dialog.Title className="gn-headline-sm text-center">
            {title}
          </Dialog.Title>

          <p className="gn-text text-center">{description}</p>

          <Formik
            initialValues={{
              email: undefined,
              password: undefined,
              importCleverProfiles: oauthInitiator === OAUTH_INITIATORS.CLEVER,
            }}
            onSubmit={(userCredentials) =>
              login({
                email: email || userCredentials.email,
                password: userCredentials.password,
                importCleverProfiles: userCredentials.importCleverProfiles,
              })
            }
          >
            {({ values, errors, setFieldValue, handleBlur }) => (
              <Form method="post" noValidate>
                <div className="mt-6 space-y-6">
                  {error ? (
                    <AlertBox text={error} className="px-2 py-sm" />
                  ) : null}

                  <div className="flex flex-col bg-white rounded-lg space-y-4">
                    {!email ? (
                      <EmailField
                        name={fields.email}
                        label="Email address"
                        autoFocus={true}
                        validateAsync={async (value) => {
                          // run validation only when email value changes
                          if (values[fields.email] !== value) {
                            try {
                              await validateEmail(value);
                              return undefined;
                            } catch (emailValidationError) {
                              if (emailValidationError?.code === 422) {
                                const {
                                  error: errorMessage,
                                } = await emailValidationError.response.json();
                                return errorMessage;
                              }
                              return undefined;
                            }
                          }
                          return errors[fields.email];
                        }}
                      />
                    ) : null}

                    <div className="space-y-1">
                      <PasswordField
                        name={fields.password}
                        label="Password"
                        validate={(value) => {
                          if (value && value.length < 6) {
                            return "Your password must be at least 6 characters";
                          }
                          if (/\s/.test(value)) {
                            return "Your password cannot contain spaces";
                          }
                          return undefined;
                        }}
                      />
                      <div>
                        <Link
                          to={`/${ROUTE_PREFIX.FORGOT_PASSWORD}`}
                          className="text-gray-700 text-[12px] underline cursor-pointer"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {/**
                           * A workaround to avoid pressing twice to leave the form.
                           * https://github.com/redux-form/redux-form/issues/860#issuecomment-625254444
                           */}
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                    {oauthInitiator === OAUTH_INITIATORS.CLEVER_SIGNUP ? (
                      <Checkbox
                        name={fields.importCleverProfiles}
                        label="Would you like to sync classes from Clever?"
                        description="This will add Clever classes to your account."
                        onChange={(val) =>
                          setFieldValue(fields.importCleverProfiles, val)
                        }
                        checked={values.importCleverProfiles}
                        onBlur={handleBlur}
                      />
                    ) : null}
                  </div>

                  <div className="flex flex-row space-x-4 mt-6">
                    <Button
                      variant={BUTTON_VARIANTS.darkOutline}
                      size={BUTTON_SIZES.lg}
                      className="w-full"
                      disabled={isLoading}
                      onPress={onClose}
                    >
                      Nevermind
                    </Button>
                    <Button
                      variant={BUTTON_VARIANTS.dark}
                      size={BUTTON_SIZES.lg}
                      type="submit"
                      disabled={isLoading}
                      className="w-full"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function RegisterViaGoogle() {
  const { user } = useUser();
  const { intendedPath } = useIntendedPath();
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [connectionCredentials, setConnectionCredentials] = React.useState();

  const googleContext = JSON.stringify({
    intended_path: intendedPath,
    oauth_initiator: OAUTH_INITIATORS.GOOGLE_SIGNUP,
  });
  const googleURL = `${process.env.NEXT_PUBLIC_GOOGLE_OAUTH_URL}?response_type=code&redirect_uri=${process.env.NEXT_PUBLIC_API_BASEURL}/universe/v1/oauth/callback/google&client_id=${process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}&scope=https%3A//www.googleapis.com/auth/userinfo.profile+https%3A//www.googleapis.com/auth/userinfo.email&state=${googleContext}`;
  const cleverContext = JSON.stringify({
    intended_path: intendedPath,
    oauth_initiator: OAUTH_INITIATORS.CLEVER_LOGIN,
  });
  let cleverURL = `${process.env.NEXT_PUBLIC_CLEVER_OAUTH_URL}?response_type=code&redirect_uri=${process.env.NEXT_PUBLIC_API_BASEURL}/universe/v1/oauth/callback/clever&client_id=${process.env.NEXT_PUBLIC_CLEVER_CLIENT_ID}&state=${cleverContext}`;
  if (process.env.NEXT_PUBLIC_CLEVER_DISTRICT_ID) {
    cleverURL += `&district_id=${process.env.NEXT_PUBLIC_CLEVER_DISTRICT_ID}`;
  }

  const {
    email,
    accountFound,
    googleError,
    oauthInitiator,
    existingCleverConnection,
  } = camelizeKeys(router.query);
  const fields = {
    termsOfService: "termsOfService",
    privacyPolicy: "privacyPolicy",
    allowMarketingEmails: "allowMarketingEmails",
    importCleverProfiles: "importCleverProfiles",
  };

  const { logEvent: logGoogleSignUpSubmissionEvent } = useLogEvent({
    event: "Google Sign Up Submission",
    properties: {
      sourceElement: SECTIONS_TYPES.GOOGLE,
    },
    options: {
      referrer: () => ({}),
    },
  });

  const { logEvent: logAgeGateFailedEvent } = useLogEvent({
    event: "Age Gate Failed",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => ({
        sourceElement: SECTIONS_TYPES.CREATE_ACCOUNT,
      }),
    },
  });

  const { logEvent: logSignUpWithGoogleStartedEvent } = useLogEvent({
    event: "Google Sign Up Started",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => ({
        sourceElement: SECTIONS_TYPES.CREATE_ACCOUNT,
      }),
    },
  });

  const { mutate: register } = useMutation(
    async (formData) => {
      const recaptchaToken = await executeRecaptcha("register");
      return client.register({ ...formData, recaptchaToken });
    },
    {
      onSuccess: () => {
        logGoogleSignUpSubmissionEvent();
        router.replace(
          concatenateQueryParams(`/${ROUTE_PREFIX.PROFILES}`, {
            registrationInitiator: REGISTRATION_INITIATORS.GOOGLE,
            referrer: JSURL.stringify({
              pathname: router.pathname,
              sourceElement: SECTIONS_TYPES.GOOGLE,
              sourceName: SECTIONS_TYPES.CREATE_ACCOUNT,
            }),
            ...router.query,
          }),
          ROUTE_PREFIX.PROFILES,
        );
      },
    },
  );

  return (
    <>
      {googleError === SSO_ERRORS.SIGNUP ? (
        <AlertBox
          text="Sorry! We're experiencing some issues with Google right now. Please try again later."
          className="px-3 py-sm"
        />
      ) : null}

      <SSOButton
        ageGate={true}
        onAgeGateFailed={logAgeGateFailedEvent}
        variantStyle="google"
        onPress={async () => {
          try {
            await logSignUpWithGoogleStartedEvent();
          } catch (e) {
            /* empty */
          } finally {
            window.location.href = googleURL;
          }
        }}
      >
        Sign Up with Google
      </SSOButton>

      {/* Start of creating new GN account via Google Dialog */}
      <Dialog.Root
        className="flex flex-col bg-gray-900 justify-normal"
        isOpen={
          accountFound === "false" &&
          oauthInitiator === OAUTH_INITIATORS.GOOGLE_SIGNUP
        }
      >
        <Dialog.Title className="gn-headline-md text-white text-center space-y-8">
          <GoNoodleHeader />
          <p>Let&apos;s Get GoNoodling!</p>
        </Dialog.Title>
        <Dialog.Content className="w-full md:w-screen-xs p-md mt-10 mb-8 bg-white rounded-lg transform transition-all">
          <Formik
            initialValues={{
              termsOfService: false,
              privacyPolicy: false,
              allowMarketingEmails: user.isUserInUsa,
              importCleverProfiles: false,
            }}
            validate={(values) => {
              const errors = {};
              const userAgreementSchema = Joi.object({
                termsOfService: Joi.bool().valid(true).messages({
                  "any.only": "Please agree to our terms of service",
                }),
                privacyPolicy: Joi.bool().valid(true).messages({
                  "any.only": "Please agree to our privacy policy",
                }),
                allowMarketingEmails: Joi.bool(),
              });

              const { error } = userAgreementSchema.validate(values, {
                abortEarly: false,
                allowUnknown: true,
                errors: {
                  label: "path",
                },
              });

              if (error) {
                forEach(error.details, (errorDetail) => {
                  errors[errorDetail.context.key] = errorDetail.message;
                });
              }

              return errors;
            }}
            onSubmit={({ allowMarketingEmails }) => {
              register({
                email,
                allowMarketingEmails,
              });
            }}
          >
            {({
              values,
              touched,
              errors,
              handleBlur,
              setFieldValue,
              submitForm,
            }) => (
              <Form method="post" noValidate>
                <div className="flex flex-col space-y-md p-4">
                  <div className="space-y-4">
                    <Checkbox
                      name={fields.termsOfService}
                      label={
                        <>
                          I have read and agree to the{" "}
                          <TermsOfServiceLink className="text-purple" />
                        </>
                      }
                      ariaLabel="I have read and agree to the Terms of Service"
                      required
                      onChange={(val) =>
                        setFieldValue(fields.termsOfService, val)
                      }
                      checked={values.termsOfService}
                      error={touched.termsOfService && errors.termsOfService}
                      onBlur={handleBlur}
                    />
                    <Checkbox
                      name={fields.privacyPolicy}
                      label={
                        <>
                          I have read and agree to the{" "}
                          <PrivacyPolicyLink className="text-purple" />
                        </>
                      }
                      ariaLabel="I have read and agree to the Privacy Policy"
                      required
                      onChange={(val) =>
                        setFieldValue(fields.privacyPolicy, val)
                      }
                      checked={values.privacyPolicy}
                      error={touched.privacyPolicy && errors.privacyPolicy}
                      onBlur={handleBlur}
                    />
                    <Checkbox
                      name={fields.allowMarketingEmails}
                      label="I’d like weekly emails from GoNoodle"
                      description="Become a GoNoodle Insider!"
                      onChange={(val) =>
                        setFieldValue(fields.allowMarketingEmails, val)
                      }
                      checked={values.allowMarketingEmails}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="flex flex-col space-y-6">
                    <Button
                      variant={BUTTON_VARIANTS.vivid}
                      size={BUTTON_SIZES.lg}
                      className="w-full"
                      onPress={() => {
                        submitForm();
                      }}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Root>
      {/* End of creating new GN account via Google Dialog */}

      {/* Already connected account to Clever */}
      <Dialog.Root
        className="flex flex-col bg-gray-900 justify-normal"
        isOpen={existingCleverConnection === "true"}
      >
        <Dialog.Title className="gn-headline-md text-white text-center">
          <GoNoodleHeader className="mb-lg" />
          Already GoNoodling?
        </Dialog.Title>
        <Dialog.Content className="w-full md:w-screen-xs p-md mt-14 mb-8 bg-white rounded-lg text-center transform transition-all">
          <div className="flex flex-col space-y-md">
            <p className="gn-text">
              You are connected by Clever already.
              <br />
            </p>
            <div className="flex flex-col space-y-sm">
              <Button
                variant={BUTTON_VARIANTS.dark}
                size={BUTTON_SIZES.md}
                onPress={() => {
                  window.location.href = cleverURL;
                }}
              >
                Log In to my GoNoodle Account with Clever
              </Button>

              <RedirectToRegistrationButton
                variant={BUTTON_VARIANTS.darkOutline}
                size={BUTTON_SIZES.md}
              >
                Never mind, I’ll sign up using a different email
              </RedirectToRegistrationButton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      {/* Start of connecting existing GN account Dialog to Google */}
      <Dialog.Root
        className="flex flex-col bg-gray-900 justify-normal"
        isOpen={
          accountFound === "true" &&
          oauthInitiator === OAUTH_INITIATORS.GOOGLE_SIGNUP &&
          !existingCleverConnection
        }
      >
        <Dialog.Title className="gn-headline-md text-white text-center">
          <GoNoodleHeader className="mb-lg" />
          Already GoNoodling?
        </Dialog.Title>
        <Dialog.Content className="w-full md:w-screen-xs p-md mt-14 mb-8 bg-white rounded-lg text-center transform transition-all">
          <div className="flex flex-col space-y-md">
            <p className="gn-text">
              We found a GoNoodle account for
              <br />
              <span className=" font-extrabold text-black">{email}</span>
              <br />
              Would you like to connect this account to Google?
            </p>
            <div className="flex flex-col space-y-sm">
              <Button
                variant={BUTTON_VARIANTS.dark}
                size={BUTTON_SIZES.md}
                onPress={() =>
                  setConnectionCredentials({
                    email,
                  })
                }
              >
                Yes, connect this account to Google
              </Button>

              <RedirectToRegistrationButton
                variant={BUTTON_VARIANTS.darkOutline}
                size={BUTTON_SIZES.md}
              >
                Never mind, I’ll sign up using a different email
              </RedirectToRegistrationButton>

              <Pressable
                className="underline gn-text-sm"
                onPress={() =>
                  setConnectionCredentials({
                    email: undefined,
                  })
                }
              >
                Connect a different GoNoodle account to google
              </Pressable>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
      {/* End of connecting existing GN account Dialog to Google */}

      {connectionCredentials ? (
        <LoginDialog
          email={connectionCredentials.email}
          title={
            connectionCredentials.email
              ? "Enter your password"
              : "Enter your log in information"
          }
          description={
            connectionCredentials.email ? (
              <>
                We found a GoNoodle account for
                <br />
                <span className=" font-extrabold text-black">{email}</span>
                <br />
                Would you like to connect this account to Google?
              </>
            ) : (
              "To connect an existing GoNoodle account to Google, please enter your GoNoodle login email and password:"
            )
          }
          onClose={() => setConnectionCredentials(undefined)}
        />
      ) : null}
    </>
  );
}

function RegisterViaClever() {
  const { user } = useUser();
  const { intendedPath } = useIntendedPath();
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [connectionCredentials, setConnectionCredentials] = React.useState();

  const googleContext = JSON.stringify({
    intended_path: intendedPath,
    oauth_initiator: OAUTH_INITIATORS.GOOGLE_SIGNUP,
  });
  const googleURL = `${process.env.NEXT_PUBLIC_GOOGLE_OAUTH_URL}?response_type=code&redirect_uri=${process.env.NEXT_PUBLIC_API_BASEURL}/universe/v1/oauth/callback/google&client_id=${process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}&scope=https%3A//www.googleapis.com/auth/userinfo.profile+https%3A//www.googleapis.com/auth/userinfo.email&state=${googleContext}`;
  const cleverContext = JSON.stringify({
    intended_path: intendedPath,
    oauth_initiator: OAUTH_INITIATORS.CLEVER_SIGNUP,
  });
  let cleverURL = `${process.env.NEXT_PUBLIC_CLEVER_OAUTH_URL}?response_type=code&redirect_uri=${process.env.NEXT_PUBLIC_API_BASEURL}/universe/v1/oauth/callback/clever&client_id=${process.env.NEXT_PUBLIC_CLEVER_CLIENT_ID}&state=${cleverContext}`;
  if (process.env.NEXT_PUBLIC_CLEVER_DISTRICT_ID) {
    cleverURL += `&district_id=${process.env.NEXT_PUBLIC_CLEVER_DISTRICT_ID}`;
  }

  const {
    email,
    accountFound,
    cleverError,
    oauthInitiator,
    existingGoogleConnection,
  } = camelizeKeys(router.query);
  const fields = {
    termsOfService: "termsOfService",
    privacyPolicy: "privacyPolicy",
    allowMarketingEmails: "allowMarketingEmails",
    importCleverProfiles: "importCleverProfiles",
  };

  const { logEvent: logCleverSignUpSubmissionEvent } = useLogEvent({
    event: "Clever Sign Up Submission",
    properties: {
      sourceElement: SECTIONS_TYPES.CLEVER,
    },
    options: {
      referrer: () => ({}),
    },
  });

  const { logEvent: logAgeGateFailedEvent } = useLogEvent({
    event: "Age Gate Failed",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => ({
        sourceElement: SECTIONS_TYPES.CREATE_ACCOUNT,
      }),
    },
  });

  const { logEvent: logSignUpWithCleverStartedEvent } = useLogEvent({
    event: "Clever Sign Up Started",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => ({
        sourceElement: SECTIONS_TYPES.CREATE_ACCOUNT,
      }),
    },
  });

  const { mutate: register } = useMutation(
    async (formData) => {
      const recaptchaToken = await executeRecaptcha("register");
      return client.register({ ...formData, recaptchaToken });
    },
    {
      onSuccess: () => {
        logCleverSignUpSubmissionEvent();
        router.replace(
          concatenateQueryParams(`/${ROUTE_PREFIX.PROFILES}`, {
            registrationInitiator: REGISTRATION_INITIATORS.CLEVER,
            referrer: JSURL.stringify({
              pathname: router.pathname,
              sourceElement: SECTIONS_TYPES.CLEVER,
              sourceName: SECTIONS_TYPES.CREATE_ACCOUNT,
            }),
            ...router.query,
          }),
          ROUTE_PREFIX.PROFILES,
        );
      },
    },
  );

  return (
    <>
      {cleverError === SSO_ERRORS.SIGNUP ? (
        <AlertBox
          text="Sorry! We're experiencing some issues with Clever right now. Please try again later."
          className="px-3 py-sm"
        />
      ) : null}

      <SSOButton
        ageGate={true}
        onAgeGateFailed={logAgeGateFailedEvent}
        variantStyle="clever"
        onPress={async () => {
          try {
            await logSignUpWithCleverStartedEvent();
          } catch (e) {
            /* empty */
          } finally {
            window.location.href = cleverURL;
          }
        }}
      >
        Sign Up with Clever
      </SSOButton>

      {/* Start of creating new GN account via Clever Dialog */}
      <Dialog.Root
        className="flex flex-col bg-gray-900 justify-normal"
        isOpen={
          accountFound === "false" &&
          oauthInitiator === OAUTH_INITIATORS.CLEVER_SIGNUP
        }
      >
        <Dialog.Title className="gn-headline-md text-white text-center space-y-8">
          <GoNoodleHeader />
          <p>Let&apos;s Get GoNoodling!</p>
        </Dialog.Title>
        <Dialog.Content className="w-full md:w-screen-xs p-md mt-10 mb-8 bg-white rounded-lg transform transition-all">
          <Formik
            initialValues={{
              termsOfService: false,
              privacyPolicy: false,
              allowMarketingEmails: user.isUserInUsa,
            }}
            validate={(values) => {
              const errors = {};
              const userAgreementSchema = Joi.object({
                termsOfService: Joi.bool().valid(true).messages({
                  "any.only": "Please agree to our terms of service",
                }),
                privacyPolicy: Joi.bool().valid(true).messages({
                  "any.only": "Please agree to our privacy policy",
                }),
                allowMarketingEmails: Joi.bool(),
              });

              const { error } = userAgreementSchema.validate(values, {
                abortEarly: false,
                allowUnknown: true,
                errors: {
                  label: "path",
                },
              });

              if (error) {
                forEach(error.details, (errorDetail) => {
                  errors[errorDetail.context.key] = errorDetail.message;
                });
              }

              return errors;
            }}
            onSubmit={({ allowMarketingEmails, importCleverProfiles }) => {
              register({
                email,
                allowMarketingEmails,
                importCleverProfiles,
              });
            }}
          >
            {({
              values,
              touched,
              errors,
              handleBlur,
              setFieldValue,
              submitForm,
            }) => (
              <Form method="post" noValidate>
                <div className="flex flex-col space-y-md p-4">
                  <div className="space-y-4">
                    <Checkbox
                      name={fields.termsOfService}
                      label={
                        <>
                          I have read and agree to the{" "}
                          <TermsOfServiceLink className="text-purple" />
                        </>
                      }
                      ariaLabel="I have read and agree to the Terms of Service"
                      required
                      onChange={(val) =>
                        setFieldValue(fields.termsOfService, val)
                      }
                      checked={values.termsOfService}
                      error={touched.termsOfService && errors.termsOfService}
                      onBlur={handleBlur}
                    />
                    <Checkbox
                      name={fields.privacyPolicy}
                      label={
                        <>
                          I have read and agree to the{" "}
                          <PrivacyPolicyLink className="text-purple" />
                        </>
                      }
                      ariaLabel="I have read and agree to the Privacy Policy"
                      required
                      onChange={(val) =>
                        setFieldValue(fields.privacyPolicy, val)
                      }
                      checked={values.privacyPolicy}
                      error={touched.privacyPolicy && errors.privacyPolicy}
                      onBlur={handleBlur}
                    />
                    <Checkbox
                      name={fields.allowMarketingEmails}
                      label="I’d like weekly emails from GoNoodle"
                      description="Become a GoNoodle Insider!"
                      onChange={(val) =>
                        setFieldValue(fields.allowMarketingEmails, val)
                      }
                      checked={values.allowMarketingEmails}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="flex flex-col space-y-6">
                    <Button
                      variant={BUTTON_VARIANTS.vivid}
                      size={BUTTON_SIZES.lg}
                      className="w-full"
                      onPress={() => {
                        setFieldValue(fields.importCleverProfiles, true);
                        submitForm();
                      }}
                    >
                      Sign Up and Sync Classes!
                    </Button>
                    <Button
                      variant={BUTTON_VARIANTS.darkOutline}
                      size={BUTTON_SIZES.md}
                      onPress={() => {
                        setFieldValue(fields.importCleverProfiles, false);
                        submitForm();
                      }}
                    >
                      Sign Up without Syncing Classes
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Root>
      {/* End of creating new GN account via Clever Dialog */}

      {/* Already connected account to Clever */}
      <Dialog.Root
        className="flex flex-col bg-gray-900 justify-normal"
        isOpen={existingGoogleConnection === "true"}
      >
        <Dialog.Title className="gn-headline-md text-white text-center">
          <GoNoodleHeader className="mb-lg" />
          Already GoNoodling?
        </Dialog.Title>
        <Dialog.Content className="w-full md:w-screen-xs p-md mt-14 mb-8 bg-white rounded-lg text-center transform transition-all">
          <div className="flex flex-col space-y-md">
            <p className="gn-text">
              You are connected by Google already.
              <br />
            </p>
            <div className="flex flex-col space-y-sm">
              <Button
                variant={BUTTON_VARIANTS.dark}
                size={BUTTON_SIZES.md}
                onPress={() => {
                  window.location.href = googleURL;
                }}
              >
                Log In to my GoNoodle Account with Google
              </Button>

              <RedirectToRegistrationButton
                variant={BUTTON_VARIANTS.darkOutline}
                size={BUTTON_SIZES.md}
              >
                Never mind, I’ll sign up using a different email
              </RedirectToRegistrationButton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      {/* Start of connecting existing GN account Dialog to Clever */}
      <Dialog.Root
        className="flex flex-col bg-gray-900 justify-normal"
        isOpen={
          accountFound === "true" &&
          oauthInitiator === OAUTH_INITIATORS.CLEVER_SIGNUP &&
          !existingGoogleConnection
        }
      >
        <Dialog.Title className="gn-headline-md text-white text-center">
          <GoNoodleHeader className="mb-lg" />
          Already GoNoodling?
        </Dialog.Title>
        <Dialog.Content className="w-full md:w-screen-xs p-md mt-14 mb-8 bg-white rounded-lg text-center transform transition-all">
          <div className="flex flex-col space-y-md">
            <p className="gn-text">
              We found a GoNoodle account for
              <br />
              <span className=" font-extrabold text-black">{email}</span>
              <br />
              Would you like to connect this account to Clever?
            </p>
            <div className="flex flex-col space-y-sm">
              <Button
                variant={BUTTON_VARIANTS.dark}
                size={BUTTON_SIZES.md}
                onPress={() =>
                  setConnectionCredentials({
                    email,
                  })
                }
              >
                Yes, connect this account to Clever
              </Button>

              <RedirectToRegistrationButton
                variant={BUTTON_VARIANTS.darkOutline}
                size={BUTTON_SIZES.md}
              >
                Never mind, I’ll sign up using a different email
              </RedirectToRegistrationButton>

              <Pressable
                className="underline gn-text-sm"
                onPress={() =>
                  setConnectionCredentials({
                    email: undefined,
                  })
                }
              >
                Connect a different GoNoodle account to clever
              </Pressable>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
      {/* End of connecting existing GN account Dialog to Clever */}

      {connectionCredentials ? (
        <LoginDialog
          email={connectionCredentials.email}
          title={
            connectionCredentials.email
              ? "Enter your password"
              : "Enter your log in information"
          }
          description={
            connectionCredentials.email ? (
              <>
                We found a GoNoodle account for
                <br />
                <span className=" font-extrabold text-black">{email}</span>
                <br />
                Would you like to connect this account to Clever?
              </>
            ) : (
              "To connect an existing GoNoodle account to Clever, please enter your GoNoodle login email and password:"
            )
          }
          onClose={() => setConnectionCredentials(undefined)}
        />
      ) : null}
    </>
  );
}

const UserCredentials = ({
  onComplete,
  email,
  password,
  dateOfBirth,
  termsOfService,
  privacyPolicy,
  allowMarketingEmails,
}) => {
  const fields = {
    email: "email",
    password: "password",
    termsOfService: "termsOfService",
    privacyPolicy: "privacyPolicy",
    allowMarketingEmails: "allowMarketingEmails",
    dateOfBirth: "dateOfBirth",
  };

  const router = useRouter();
  const { user } = useUser();
  const SSOEnabled = user.features?.clever || user.features?.googleSso;
  const [locked, setLocked] = React.useState(false);
  const { validateEmail } = useValidateEmail();

  const { logEvent: logSubmissionEvent } = useLogEvent({
    event: "Create Account Email Submission",
    properties: {},
    options: {
      referrer: () => ({
        ...getUtmParamsFromQueryParams(router.query),
      }),
    },
  });

  useLogEvent({
    event: "Age Gate Failed",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => ({
        sourceElement: SECTIONS_TYPES.CREATE_ACCOUNT,
      }),
      enabled: locked,
    },
  });

  return (
    <div className="w-full flex flex-col px-4 py-lg md:px-6 lg:mx-auto lg:w-3/5">
      <div className="text-white text-center space-y-3 mb-10">
        <h1 className="gn-headline-md">
          {locked ? "Sorry, buddy!" : "Hello, Grown Up Person!"}
        </h1>
        <p className="gn-text">
          {locked
            ? "You need a grown up to help you."
            : "Creating an account unlocks GoNoodle in the classroom and at home with access to your favorite videos, educator resources, and activities"}
        </p>
      </div>

      {locked === true ? null : (
        <Formik
          initialValues={{
            email,
            password,
            dateOfBirth,
            termsOfService: !!termsOfService,
            privacyPolicy: !!privacyPolicy,
            allowMarketingEmails: !!allowMarketingEmails,
          }}
          validate={(values) => {
            const errors = {};
            const userCredentialsSchema = Joi.object({
              termsOfService: Joi.bool().valid(true).messages({
                "any.only": "Please agree to our terms of service",
              }),
              privacyPolicy: Joi.bool().valid(true).messages({
                "any.only": "Please agree to our privacy policy",
              }),
              allowMarketingEmails: Joi.bool(),
            });

            const { error } = userCredentialsSchema.validate(values, {
              abortEarly: false,
              allowUnknown: true,
              errors: {
                label: "path",
              },
            });

            if (error) {
              forEach(error.details, (errorDetail) => {
                errors[errorDetail.context.key] = errorDetail.message;
              });
            }

            return errors;
          }}
          onSubmit={(formData) => {
            const { dateOfBirth: date, ...rest } = formData;
            const age =
              new Date(new Date() - date.toDate()).getFullYear() - 1970;

            if (age < 18 || age > 100) {
              setLocked(true);
            } else {
              logSubmissionEvent({
                newsletterSignup: !!formData.allowMarketingEmails,
              });
              onComplete(rest);
            }
          }}
        >
          {({ values, touched, errors, handleBlur, setFieldValue }) => (
            <Form method="post" noValidate>
              <div
                className={`
            ${!SSOEnabled && "md:w-screen-xs mx-auto"}
             md:w-full space-y-6`}
              >
                <div className="flex flex-col md:flex-row px-4 lg:px-9 py-11 bg-white rounded-lg">
                  <div className="flex flex-col flex-1 space-y-md">
                    <div className="space-y-4">
                      <EmailField
                        name={fields.email}
                        label="Enter your email address"
                        autoFocus={true}
                        autoComplete="off"
                        errorMessage={touched.email && errors.email}
                        validateAsync={async (value) => {
                          // run validation only when email value changes
                          if (values[fields.email] !== email) {
                            try {
                              await validateEmail(value);
                              return undefined;
                            } catch (emailValidationError) {
                              if (emailValidationError?.code === 403) {
                                const {
                                  error: errorMessage,
                                } = await emailValidationError.response.json();
                                return (
                                  <span>
                                    {errorMessage}

                                    <RedirectToLoginLink
                                      queryParams={{
                                        referrer: {
                                          pathname: router.pathname,
                                          query: router.query,
                                          sourceElement:
                                            SECTIONS_TYPES.CREATE_ACCOUNT,
                                          sourceName:
                                            SECTIONS_TYPES.CREATE_ACCOUNT,
                                        },
                                      }}
                                    >
                                      <p className="text-gray-700 underline cursor-pointer">
                                        Log In
                                      </p>
                                    </RedirectToLoginLink>
                                  </span>
                                );
                              }
                              if (emailValidationError?.code === 422) {
                                const {
                                  error: errorMessage,
                                } = await emailValidationError.response.json();
                                return errorMessage;
                              }
                              return "Sorry, we’re having trouble with that. Please try again!";
                            }
                          }
                          return errors[fields.email];
                        }}
                      />
                      <PasswordField
                        name={fields.password}
                        label="Choose a password"
                        description="Please use at least 6 characters"
                        validate={(value) => {
                          if (/\s/.test(value)) {
                            return "Your password cannot contain spaces";
                          }

                          if (value && value.length < 6) {
                            return "Your password must be at least 6 characters";
                          }
                          return undefined;
                        }}
                      />
                      <DateField
                        name={fields.dateOfBirth}
                        label="Date of Birth"
                        description="We need to confirm you're an adult. We do not keep or share this information with anyone"
                        required={true}
                      />
                    </div>

                    <div className="space-y-4">
                      <Checkbox
                        name={fields.termsOfService}
                        label={
                          <>
                            I have read and agree to the{" "}
                            <TermsOfServiceLink className="text-purple" />
                          </>
                        }
                        ariaLabel="I have read and agree to the Terms of Service"
                        required
                        onChange={(val) =>
                          setFieldValue(fields.termsOfService, val)
                        }
                        checked={values.termsOfService}
                        error={touched.termsOfService && errors.termsOfService}
                        onBlur={handleBlur}
                      />
                      <Checkbox
                        name={fields.privacyPolicy}
                        label={
                          <>
                            I have read and agree to the{" "}
                            <PrivacyPolicyLink className="text-purple" />
                          </>
                        }
                        ariaLabel="I have read and agree to the Privacy Policy"
                        required
                        onChange={(val) =>
                          setFieldValue(fields.privacyPolicy, val)
                        }
                        checked={values.privacyPolicy}
                        error={touched.privacyPolicy && errors.privacyPolicy}
                        onBlur={handleBlur}
                      />
                      <Checkbox
                        name={fields.allowMarketingEmails}
                        label="I’d like weekly emails from GoNoodle"
                        description="Become a GoNoodle Insider!"
                        onChange={(val) =>
                          setFieldValue(fields.allowMarketingEmails, val)
                        }
                        checked={values.allowMarketingEmails}
                        onBlur={handleBlur}
                      />
                    </div>

                    <Button
                      variant={BUTTON_VARIANTS.vivid}
                      size={BUTTON_SIZES.lg}
                      type="submit"
                      className="mt-6"
                    >
                      Next
                    </Button>
                  </div>
                  {SSOEnabled && (
                    <>
                      <div className="hidden md:block mx-7 border-r-sm border-gray-500" />

                      <div className="flex flex-col space-y-6 flex-1 mt-6 md:mt-0">
                        <div className="flex flex-row items-center ">
                          <span className="md:hidden flex-1 border-t-sm border-dashed border-gray-500" />
                          <p className="text-center font-bold gn-text-sm text-gray-500 uppercase mx-auto">
                            Or create an account with
                          </p>
                          <span className="md:hidden flex-1 border-t-sm border-dashed border-gray-500" />
                        </div>

                        {user.features?.clever && <RegisterViaClever />}
                        {user.features?.googleSso && <RegisterViaGoogle />}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex flex-row justify-center space-x-sm text-gray-300 gn-text-xs ">
                  <PrivacyPolicyLink className="ml-5" />
                  <p> | </p>
                  <TermsOfServiceLink />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}

      <div
        className={`flex flex-col md:w-screen-xs p-md bg-white rounded-lg space-y-md self-center ${
          !locked ? "hidden" : ""
        }`}
      >
        <div className="self-center mr-4">
          <img
            className="w-[150px] h-[180px] object-contain"
            width={150}
            height={180}
            src={`${URLS.GN_ASSETS_BASE_URL}/web_public/McPufferson-L1-sad.svg`}
            alt=""
          />
        </div>
        <Button
          variant={BUTTON_VARIANTS.dark}
          size={BUTTON_SIZES.md}
          onPress={() => {
            router.push(ROUTE_PATHS.HOME);
          }}
        >
          Watch GoNoodle Videos
        </Button>
      </div>

      <div className="text-center mt-6">
        <p className="text-sm text-white">
          Protected by reCAPTCHA{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy
          </a>
          &nbsp;and&nbsp;
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms
          </a>
        </p>
      </div>
    </div>
  );
};

const ProfileTypes = ({ onComplete }) => {
  const router = useRouter();

  const { logEvent: logSubmissionEvent } = useLogEvent({
    event: "Create Account Profile Selection",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => ({}),
    },
  });

  return (
    <div className="flex flex-col items-center pb-8 px-sm">
      <div className="flex flex-col">
        <h1 className="gn-headline-md text-white text-center mb-lg">
          Where are you using GoNoodle?
        </h1>
        <div className="space-y-xl">
          <Formik
            initialValues={{
              profileType: undefined,
            }}
            validate={({ profileType }) => {
              if (!profileType) {
                return false;
              }
              return true;
            }}
            onSubmit={(formData) => {
              logSubmissionEvent({ profileType: formData.profileType });
              onComplete(formData);
            }}
          >
            {({ setFieldValue, submitForm }) => (
              <Form method="post" noValidate>
                <div className="space-y-xl md:w-[490px]">
                  <ProfileType
                    type={PROFILE_TYPES.CLASSROOM}
                    onClick={(type) => {
                      setFieldValue("profileType", type);
                      submitForm();
                    }}
                  />
                  <hr className="md:-mx-xl" />
                  <ProfileType
                    type={PROFILE_TYPES.KID_HOME}
                    onClick={(type) => {
                      setFieldValue("profileType", type);
                      submitForm();
                    }}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

const ClassProfile = ({ onComplete, isSubmitting }) => {
  const alert = Toast.useAlert();
  const [searchTerm, setSearchTerm] = React.useState();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fields = {
    school: "school",
    numberOfKids: "numberOfKids",
    className: "className",
    grade: "grade",
  };

  const { schools, schoolsError, isSchoolsLoading } = useSchoolsFilterQuery(
    debouncedSearchTerm,
  );

  const { grades, isGradesLoading, gradesError } = useGradesQuery();

  const schoolsWithAdditionalOptions = React.useMemo(() => {
    if (schools && !isSchoolsLoading && !schoolsError) {
      return concat(schools, [
        {
          id: null,
          name: "School Not Found",
        },
      ]);
    }
    return [];
  }, [schools, isSchoolsLoading, schoolsError]);

  if (schoolsError) {
    alert.error("Something went wrong. Please reload page.");
  }

  return (
    <div className="flex flex-row-reverse pb-8 px-sm">
      <Formik
        onSubmit={onComplete}
        initialValues={{
          school: undefined,
          numberOfKids: NaN,
          className: undefined,
          grade: undefined,
        }}
        validate={(values) => {
          const errors = {};
          const classProfileSchema = Joi.object({
            school: Joi.object({
              id: Joi.any(),
              name: Joi.string().required(),
              address: Joi.any(),
            })
              .required()
              .messages({
                "any.required": "Please select your school",
              }),
            numberOfKids: Joi.number().min(1).max(100).required().messages({
              "any.required": "Please enter the number of kids",
              "number.base": "Please enter the number of kids",
              "number.min": "Please enter a number between 1 and 100",
              "number.max": "Please enter a number between 1 and 100",
            }),
            className: Joi.string()
              .required()
              .pattern(
                /[\u00a9\u00ae\u2000-\u3300\ud83c\ud000-\udfff\ud83d\ud000-\udfff\ud83e\ud000-\udfff]/,
                { invert: true },
              )
              .messages({
                "any.required": "Please enter your class name",
                "string.empty": "Please enter your class name",
                "string.pattern.invert.base":
                  "Please only use valid characters",
              }),
            grade: Joi.object({
              id: Joi.number().required(),
              name: Joi.string().required(),
            })
              .required()
              .messages({ "any.required": "Please select your grade" }),
          });

          const { error } = classProfileSchema.validate(values, {
            abortEarly: false,
            errors: {
              label: "path",
            },
          });

          if (error) {
            forEach(error.details, (errorDetail) => {
              errors[errorDetail.context.key] = errorDetail.message;
            });
          }

          return errors;
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form method="post" noValidate>
            <div className="md:w-screen-xs space-y-md flex flex-col p-lg mt-lg bg-white rounded-lg">
              <div className="text-center">
                <h1 className="gn-headline-md">Create Class Profile</h1>
              </div>
              <div className="space-y-sm">
                <Autocomplete
                  name={fields.school}
                  label="School"
                  options={schoolsWithAdditionalOptions}
                  required={true}
                  autoFocus={true}
                  testId="school-autocomplete"
                  error={
                    touched.school && errors.school
                      ? errors.school
                      : schoolsError
                      ? "Something went wrong try again"
                      : undefined
                  }
                  inputProps={{
                    onBlur: handleBlur,
                    autoComplete: "off",
                    spellCheck: "false",
                    endAdornment: isSchoolsLoading ? (
                      <RefreshIcon className="h-input-icon text-gray-300 animate-spin" />
                    ) : null,
                  }}
                  onChange={(selectedOption) => {
                    // This is a hack to debounce the setFieldValue call, otherwise setFieldValue causes infinite loop
                    // BUG: https://github.com/jaredpalmer/formik/issues/2858
                    setTimeout(() => {
                      setFieldValue(fields.school, selectedOption);
                    }, 0);
                  }}
                  onInputValueChange={(value) => setSearchTerm(value)}
                  renderOptions={(item, selectedOption, getItemProps) => {
                    const { name, address, id } = item;
                    if (id === null) {
                      const { onClick: onItemClick, ...rest } = getItemProps();
                      return (
                        <li {...rest}>
                          <div className="flex justify-between pl-3 pt-2 pr-xs pb-3 mt-4 border-t-sm border-pink-300 cursor-default">
                            <div className="flex-col">
                              <p className="text-xs">
                                We work with schools to keep GoNoodle awesome!
                              </p>
                              <p className="text-xs">
                                Can&apos;t find your school?{" "}
                                <span
                                  className="underline cursor-pointer"
                                  onClick={onItemClick}
                                >
                                  Continue
                                </span>
                              </p>
                            </div>
                            {selectedOption?.id === id ? (
                              <div className="self-center">
                                <CheckIcon
                                  className="h-input-icon text-gray-300"
                                  aria-hidden="true"
                                />
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li {...getItemProps()}>
                        <div className="flex justify-between pl-3 pt-2 pr-xs pb-1">
                          <div className="flex-col space-y-xs">
                            <p className="font-bold text-sm">{name}</p>
                            <p className="text-xs">{address}</p>
                          </div>
                          {selectedOption?.id === id ? (
                            <div className="self-center">
                              <CheckIcon
                                className="h-input-icon text-gray-300"
                                aria-hidden="true"
                              />
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  }}
                />
                <NumberField
                  label="Number of Kids"
                  name={fields.numberOfKids}
                  required
                  onChange={(value) =>
                    setFieldValue(fields.numberOfKids, value)
                  }
                  onBlur={() =>
                    setFieldTouched(fields.numberOfKids, true, false)
                  }
                  value={values.numberOfKids}
                  errorMessage={touched.numberOfKids && errors.numberOfKids}
                />
                <Select
                  options={grades || []}
                  name={fields.grade}
                  label="Grade"
                  value={values.grade}
                  testId="grade-select"
                  error={
                    (touched.grade && errors.grade) ||
                    (gradesError && "Something went wrong try again")
                  }
                  onChange={(selectedOption) =>
                    setFieldValue(fields.grade, selectedOption)
                  }
                  onBlur={handleBlur}
                  buttonClassNames="block w-full h-input pl-sm gn-text rounded border-sm border-gray-300 shadow-lightSM"
                  optionClassNames="pl-lg"
                  optionTextClassNames="gn-text-xs font-semibold"
                  checkmark
                  inputProps={{
                    endAdornment: isGradesLoading ? (
                      <RefreshIcon className="h-input-icon text-gray-300 animate-spin" />
                    ) : (
                      <SelectorIcon className="h-input-icon text-gray-300" />
                    ),
                  }}
                />
                <TextField
                  name={fields.className}
                  label="Give your class a fun name"
                  required
                  onChange={(value) => setFieldValue(fields.className, value)}
                  value={values.className}
                  onBlur={handleBlur}
                  errorMessage={touched.className && errors.className}
                />
              </div>
              <div>
                <Button
                  variant={BUTTON_VARIANTS.vivid}
                  size={BUTTON_SIZES.lg}
                  disabled={isSubmitting}
                  type="submit"
                  className="w-full"
                >
                  Choose your Champ
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <div className="hidden lg:block lg:absolute lg:left-0 lg:top-1/3 lg:ml-lg">
        <TalkyChamp text="Hey GoNoodlers, McPufferson here! Create a profile for each of your classrooms so you can watch me and my friends grow when your class plays GoNoodle!" />
      </div>
    </div>
  );
};

const KidProfile = ({ onComplete, isSubmitting }) => {
  const fields = {
    userName: "userName",
    grade: "grade",
  };

  const { grades, isGradesLoading, gradesError } = useGradesQuery();

  return (
    <div className="flex flex-row-reverse pb-8 px-sm">
      <Formik
        initialValues={{ userName: undefined, grade: undefined }}
        validate={(values) => {
          const errors = {};
          const kidProfileSchema = Joi.object({
            grade: Joi.object({
              id: Joi.number().required(),
              name: Joi.string().required(),
            })
              .required()
              .messages({ "any.required": "Please select your grade" }),
            userName: Joi.string()
              .required()
              .pattern(/\s/, { invert: true })
              .message("You cannot use spaces in your username")
              .pattern(
                /[\u00a9\u00ae\u2000-\u3300\ud83c\ud000-\udfff\ud83d\ud000-\udfff\ud83e\ud000-\udfff]/,
                { invert: true },
              )
              .messages({
                "string.empty": "Please enter your user name",
                "any.required": "Please enter your user name",
                "string.pattern.invert.base":
                  "Please only use valid characters",
              }),
          });

          const { error } = kidProfileSchema.validate(values, {
            abortEarly: false,
            errors: {
              label: "path",
            },
          });

          if (error) {
            forEach(error.details, (errorDetail) => {
              errors[errorDetail.context.key] = errorDetail.message;
            });
          }

          return errors;
        }}
        onSubmit={onComplete}
      >
        {({ values, touched, errors, handleBlur, setFieldValue }) => (
          <Form method="post" noValidate>
            <div className="md:w-screen-xs space-y-md flex flex-col p-lg mt-lg bg-white rounded-lg">
              <div className="text-center">
                <h1 className="gn-headline-md">Create Kid (Home) Profile</h1>
              </div>
              <div className="space-y-sm">
                <TextField
                  name={fields.userName}
                  id={fields.userName}
                  label="Username"
                  description="For privacy, choose a fun username, not your kid's actual name!"
                  required={true}
                  autoFocus={true}
                  onChange={(value) => setFieldValue(fields.userName, value)}
                  value={values.userName}
                  errorMessage={touched.userName && errors.userName}
                  onBlur={handleBlur}
                />
                <Select
                  options={grades || []}
                  id={fields.grade}
                  label="Grade"
                  value={values.grade}
                  testId="grade-select"
                  error={
                    (touched.grade && errors.grade) ||
                    (gradesError && "Something went wrong try again")
                  }
                  onChange={(selectedOption) =>
                    setFieldValue(fields.grade, selectedOption)
                  }
                  onBlur={handleBlur}
                  buttonClassNames="block w-full h-input pl-sm gn-text rounded border-sm border-gray-300 shadow-lightSM"
                  optionClassNames="pl-lg"
                  optionTextClassNames="gn-text-xs font-semibold"
                  checkmark
                  inputProps={{
                    endAdornment: isGradesLoading ? (
                      <RefreshIcon className="h-input-icon text-gray-300 animate-spin" />
                    ) : (
                      <SelectorIcon className="h-input-icon text-gray-300" />
                    ),
                  }}
                />
              </div>
              <div>
                <Button
                  variant={BUTTON_VARIANTS.vivid}
                  size={BUTTON_SIZES.lg}
                  disabled={isSubmitting}
                  type="submit"
                  className="w-full"
                >
                  Choose your Champ
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <div className="hidden lg:block lg:absolute lg:left-0 lg:top-1/3 lg:ml-lg">
        <TalkyChamp text="Hey GoNoodlers, McPufferson here! Create a profile for each kid so they can watch me and my friends grow when you play GoNoodle at home!" />
      </div>
    </div>
  );
};

const ProfileForm = ({ onComplete, error, profileType, isSubmitting }) => (
  <div className="flex flex-col items-center">
    {error ? (
      <div className="flex md:w-screen-xs">
        <AlertBox className="p-sm" text="Something went wrong" />
      </div>
    ) : null}
    {profileType === PROFILE_TYPES.CLASSROOM ? (
      <ClassProfile onComplete={onComplete} isSubmitting={isSubmitting} />
    ) : null}
    {profileType === PROFILE_TYPES.KID_HOME ? (
      <KidProfile onComplete={onComplete} isSubmitting={isSubmitting} />
    ) : null}
  </div>
);

function ChampSelectionScreen({
  onComplete,
  grade,
  school,
  numberOfKids,
  className,
  profileType,
  userName,
  isSubmitting,
}) {
  const router = useRouter();
  const defaultChampion = useChampion(CHAMP_NAMES.MC_PUFFERSON);
  const analyticsProps = React.useMemo(
    () => ({
      sourcePage: PAGES.REGISTRATION,
      sourcePageType: PAGES.REGISTRATION,
      gradeId: grade?.id,
      size: numberOfKids,
      schoolId: school?.id,
      name: className ?? userName,
    }),
    [grade, numberOfKids, school, className, userName],
  );
  const { loggedOutChampPoints } = useLoggedOutChampPoints();
  const { logEvent: logChampSelected } = useLogEvent({
    event: "Champ Selected",
    properties: {
      ...analyticsProps,
      ...getUtmParamsFromQueryParams(router.query),
    },
  });

  function handelSelectedChampionChange(champion, isRandom) {
    logChampSelected({
      champName: champion.name,
      champRandomizerUsed: isRandom,
      profileType,
    });
    onComplete({ selectedChamp: champion });
  }

  return (
    <div>
      <div className="mb-xl">
        <h1 className="font-display text-2xl lg:text-4xl text-white mb-md text-center">
          Choose your Champ
        </h1>
        {loggedOutChampPoints ? (
          <p className="text-white text-md text-center">
            Your{" "}
            <ChampPointsStamp
              size="sm"
              points={loggedOutChampPoints}
              className="px-0.5 inline-block align-middle mb-[1px]"
            />{" "}
            point{loggedOutChampPoints === 1 ? "" : "s"} will transfer to your
            new Champ.
          </p>
        ) : null}
      </div>
      <ChampionSelect
        defaultSelectedChampion={defaultChampion}
        onChange={handelSelectedChampionChange}
        disabled={isSubmitting}
        analyticsProps={{
          sourcePage: PAGES.REGISTRATION,
          sourcePageType: PAGES.REGISTRATION,
        }}
      />
    </div>
  );
}

const registrationSteps = [
  UserCredentials,
  ProfileTypes,
  ProfileForm,
  ChampSelectionScreen,
];

export default function RegistrationPage() {
  const { user } = useUser();
  const [submitForm, setSubmitForm] = React.useState(false);
  const router = useRouter();
  const { oauthInitiator } = camelizeKeys(router.query);
  const [account, setAccount] = React.useState({
    allowMarketingEmails: user.isUserInUsa,
  });
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const { redirectToIntendedPath } = useIntendedPath();

  const queryClient = useQueryClient();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    loggedOutChampPoints,
    resetLoggedOutChampPoints,
  } = useLoggedOutChampPoints();

  const { logEvent: logProfileCreatedEvent } = useLogEvent({
    event: "Profile Created",
    properties: {
      sourcePage: PAGES.REGISTRATION,
      sourcePageType: PAGES.REGISTRATION,
      ...getUtmParamsFromQueryParams(router.query),
    },
  });

  const {
    mutate: register,
    isLoading: isSubmitting,
    error,
    reset: resetRegistrationSubmission,
  } = useMutation(
    (formData) => client.register({ ...formData, loggedOutChampPoints }),
    {
      onSuccess: async (data, submittedUserData) => {
        await queryClient.invalidateQueries([QUERY_KEYS.USER]);
        queryClient.invalidateQueries([QUERY_KEYS.USER_ACCESSES]);

        logProfileCreatedEvent({
          gradeId: submittedUserData.grade.id,
          size: submittedUserData.numberOfKids,
          schoolId:
            submittedUserData.profileType === PROFILE_TYPES.CLASSROOM
              ? submittedUserData.school.id
              : undefined,
          name:
            submittedUserData.profileType === PROFILE_TYPES.CLASSROOM
              ? submittedUserData.className
              : submittedUserData.userName,
          profileType: submittedUserData.profileType,
        });
        resetLoggedOutChampPoints();
        redirectToIntendedPath(ROUTE_PATHS.HOME);
      },
    },
  );

  useLogEvent({
    event: "Create Account Flow Entered",
    options: {
      referrer: (
        { pathname, query, ...rest } = {
          sourcePage: "direct",
          sourcePageType: "direct",
          sourceName: "direct",
          sourceElement: "direct",
        },
      ) => ({
        ...rest,
        ...getSourcePageAndSourcePageTypeFromReferrer(pathname, query),
        ...getUtmParamsFromQueryParams(router.query),
      }),
      enabled: oauthInitiator === undefined, // Avoid logging when coming from Clever redirect.
    },
  });

  const onStepComplete = (formData) => {
    setAccount({
      ...account,
      ...formData,
    });

    if (currentStepIndex !== registrationSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      setSubmitForm(true);
    }
  };

  const canGoToPreviousStep =
    registrationSteps[currentStepIndex] === ProfileTypes ||
    registrationSteps[currentStepIndex] === ProfileForm;

  const { logEvent: logBackPressedEvent } = useLogEvent({
    event: "Create Account Profile Exited",
    properties: {},
    options: {
      referrer: () => ({
        sourceName: SECTIONS.REGISTRATION_BACK_BUTTON,
      }),
    },
  });

  const onBackHandler = () => {
    setCurrentStepIndex(currentStepIndex - 1);
    resetRegistrationSubmission();
    setSubmitForm(false);
    logBackPressedEvent();
  };

  React.useEffect(() => {
    let isMounted = true;
    const triggerRegistration = async () => {
      if (isMounted) {
        const token = await executeRecaptcha("register");
        register({
          ...account,
          recaptchaToken: token,
        });
      }
    };
    if (submitForm) {
      triggerRegistration();
    }
    return () => {
      isMounted = false;
    };
  }, [account, submitForm, executeRecaptcha, register]);

  const Step = React.useMemo(() => registrationSteps[currentStepIndex], [
    currentStepIndex,
  ]);

  return (
    <Layout
      pageTitle="Registration"
      pageDescription="Registration Page"
      footer={false}
      header={false}
    >
      <div className="relative flex items-center justify-center w-full px-sm">
        {canGoToPreviousStep ? (
          <button className="absolute left-0 px-sm" onClick={onBackHandler}>
            <ArrowLeftIcon className="w-7 h-[25px] text-white" />
          </button>
        ) : null}
        <GoNoodleHeader />
      </div>

      <div className="w-full flex flex-col items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepIndex}
            className="w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Step
              onComplete={onStepComplete}
              {...account}
              error={error}
              isSubmitting={isSubmitting}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </Layout>
  );
}

export const getServerSideProps = withAuthorization(async (ctx) => {
  const queryClient = new QueryClient();
  const apiClient = api(ctx);

  await queryClient.prefetchQuery([QUERY_KEYS.CHAMPIONS], () =>
    apiClient.getChampions(),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}, ROUTE_PERMISSIONS.ANONYMOUS);
